import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  password: '',
  confirmPassword: '',
  phoneCode: '+1',
  phone: '',
};

const SignUp = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formKey] = useState(() => `signup-${Date.now()}`);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showAgeNotice, setShowAgeNotice] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    setFormData(initialFormData);
    setErrors({});
    setPasswordStrength(0);
  }, []);

  const checkPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    setPasswordStrength(score);
    return score;
  };

  const getStrengthText = () => {
    const texts = ['Weak', 'Fair', 'Good', 'Strong'];
    return texts[passwordStrength - 1] || '';
  };

  const getStrengthColor = () => {
    const colors = ['#d63b3b', '#f59e0b', '#3b82f6', '#2ebd59'];
    return colors[passwordStrength - 1] || '#e2e8f0';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: id === 'username' ? value.replace(/\s+/g, '') : value,
    }));

    if (id === 'password') {
      checkPasswordStrength(value);
    }

    if (errors[id]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const username = formData.username.trim();

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Enter a valid email address';
    if (!username) newErrors.username = 'Username is required';
    else if (username.length < 3) newErrors.username = 'Username must be at least 3 characters';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const phone = formData.phoneCode && formData.phone ? formData.phoneCode + formData.phone : undefined;
      await signup({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        username: formData.username.trim(),
        phone,
      });
      setFormData(initialFormData);
      setPasswordStrength(0);
      navigate('/account/complete', { replace: true });
    } catch (err: any) {
      setErrors({ general: err.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="wave-bg" aria-hidden="true"><span></span></div>

      <div className="page-header">
        <div className="logo-bubble">
          <div className="logo">
            <img className="logo-img" src="/assets/logo2.webp" alt="Olitask" />
          </div>
        </div>
        <h1>Create your account</h1>
        <p className="subtitle">Sign up to start using Olitask</p>
      </div>

      <div className="auth-card signup-card">
        {showAgeNotice && (
          <div className="age-notice" id="ageNotice">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="#f59e0b" opacity=".2"/>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#d97706" strokeWidth="1.5" strokeLinejoin="round"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="12" cy="15" r="1" fill="#d97706"/>
            </svg>
            <p><strong>18+ only</strong><br />You must be at least 18 years old to create and use an Olitask account.</p>
            <button className="age-close" type="button" onClick={() => setShowAgeNotice(false)}>x</button>
          </div>
        )}

        {errors.general && (
          <div className="error-banner show">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{errors.general}</span>
          </div>
        )}

        <form key={formKey} onSubmit={handleSubmit} autoComplete="off">
          <div className="form-row">
            <div className={`field ${errors.firstName ? 'has-error' : ''}`}>
              <label>First Name <span className="req">*</span></label>
              <input type="text" id="firstName" name={`${formKey}-first-name`} value={formData.firstName} onChange={handleChange} autoComplete="off" />
              <div className="error-msg">{errors.firstName}</div>
            </div>
            <div className={`field ${errors.lastName ? 'has-error' : ''}`}>
              <label>Last Name <span className="req">*</span></label>
              <input type="text" id="lastName" name={`${formKey}-last-name`} value={formData.lastName} onChange={handleChange} autoComplete="off" />
              <div className="error-msg">{errors.lastName}</div>
            </div>
          </div>

          <div className={`field ${errors.email ? 'has-error' : ''}`}>
            <label>Email <span className="req">*</span></label>
            <input type="email" id="email" name={`${formKey}-email`} value={formData.email} onChange={handleChange} autoComplete="off" inputMode="email" />
            <div className="error-msg">{errors.email}</div>
          </div>

          <div className="field">
            <label>Phone <span className="opt">(optional)</span></label>
            <div className="phone-wrap">
              <input type="text" id="phoneCode" name={`${formKey}-phone-code`} maxLength={5} value={formData.phoneCode} onChange={handleChange} autoComplete="off" />
              <input type="tel" id="phone" name={`${formKey}-phone`} value={formData.phone} onChange={handleChange} autoComplete="off" inputMode="tel" />
            </div>
          </div>

          <div className={`field ${errors.username ? 'has-error' : ''}`}>
            <label>Username <span className="req">*</span></label>
            <input
              type="text"
              id="username"
              name={`${formKey}-username`}
              value={formData.username}
              onChange={handleChange}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            <div className="error-msg">{errors.username}</div>
          </div>

          <div className={`field ${errors.password ? 'has-error' : ''}`}>
            <label>Password <span className="req">*</span></label>
            <div className="pw-wrap">
              <input
                type="password"
                id="password"
                name={`${formKey}-password`}
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </div>
            {formData.password && (
              <div className="pw-strength">
                <div className="pw-strength-label" style={{ color: getStrengthColor() }}>{getStrengthText()}</div>
              </div>
            )}
            <div className="error-msg">{errors.password}</div>
          </div>

          <div className={`field ${errors.confirmPassword ? 'has-error' : ''}`}>
            <label>Confirm Password <span className="req">*</span></label>
            <div className="pw-wrap">
              <input
                type="password"
                id="confirmPassword"
                name={`${formKey}-confirm-password`}
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </div>
            <div className="error-msg">{errors.confirmPassword}</div>
          </div>

          <div className="age-notice confirm-text">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="#f59e0b" opacity=".2"/>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#d97706" strokeWidth="1.5" strokeLinejoin="round"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="12" cy="15" r="1" fill="#d97706"/>
            </svg>
            <p><strong>18+ only</strong><br />You must be at least 18 years old to create and use an Olitask account.</p>
          </div>

          <div className="btn-row">
            <Link to="/Home.html" className="btn btn-cancel">Cancel</Link>
            <button type="submit" className="btn btn-create" disabled={isLoading}>
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </form>

        <p className="signin-link">Already have an account? <Link to="/Sign-In.html">Sign In</Link></p>
      </div>
    </div>
  );
};

export default SignUp;
